import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, CalendarBlank, CalendarPlus, Check, Clock, MapPin, ShareNetwork, Warning, X } from 'phosphor-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useFavoritesIds } from '@/hooks/useFavorites';
import { hap } from '@/lib/haptics';
import { Capacitor } from '@capacitor/core';
import { openExternal } from '@/lib/native';
import { ShareEventModal } from '@/components/share/ShareEventModal';
import { requestStartupNotifications } from '@/lib/appRuntime';
import { syncPushTokenForUser } from '@/lib/pushNotifications';
import { categoryOf, AUDIENCE_LABEL } from '@/constants/categories';
import { formatDayLong, formatTime, formatRange, hasKnownTime } from '@/lib/dates';
import { getResponsiveImageProps } from '@/lib/images';
import { FallbackPoster } from '@/components/ui/FallbackPoster';
import { EditionBadge } from '@/components/ui/EditionBadge';
import { Button } from '@/components/ui/Button';
import { GoldenRule } from '@/components/ui/GoldenRule';
import { Skeleton } from '@/components/ui/Skeleton';
import { Monogram } from '@/components/ui/Monogram';
import { upperCity } from '@/lib/city';
import { TamponJyEtais } from '@/components/ui/TamponJyEtais';
import { SourceFooter } from '@/components/ui/SourceFooter';
import { EventMap } from '@/components/EventMap';
import { AdBannerDetail } from '@/components/ads/AdBannerDetail';
import { EventCard } from '@/components/EventCard';
import type { EventRow, OrganizerRow } from '@/types/database';

interface Props {
  eventId: string | null;
  onClose: () => void;
}

export function EventDetailSheet({ eventId, onClose }: Props) {
  const { user } = useAuth();
  const fav = useFavoritesIds();
  const [event, setEvent] = useState<EventRow | null>(null);
  const [org, setOrg] = useState<OrganizerRow | null>(null);
  const [also, setAlso] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [agendaNote, setAgendaNote] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) { setEvent(null); setOrg(null); setAlso([]); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      const { data: ev } = await supabase.from('events').select('*').eq('id', eventId).maybeSingle();
      if (!alive || !ev) { setLoading(false); return; }
      setEvent(ev as EventRow);
      if ((ev as EventRow).organizer_id) {
        const { data: o } = await supabase.from('organizers').select('*').eq('id', (ev as EventRow).organizer_id).maybeSingle();
        setOrg(o as OrganizerRow);
      }
      const { data: alsoData } = await supabase.from('events').select('*')
        .eq('kind', (ev as EventRow).kind).neq('id', eventId).gte('starts_at', new Date().toISOString())
        .order('starts_at').limit(3);
      setAlso((alsoData as EventRow[]) ?? []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [eventId, user?.id]);

  function share() {
    if (!event) return;
    // Ouvre l'aperçu de l'image partageable : on ne poste pas un visuel généré
    // (au fond aléatoire) sans l'avoir vu.
    hap.light();
    setShareOpen(true);
  }

  async function toggleAgenda() {
    if (!event) return;
    hap.light();
    const added = await fav.toggle(event.id); // favoris locaux en invité, en base si connecté
    if (added) {
      // C'est LE moment où la permission de notifier a du sens : on la demande
      // ici (natif), pas à froid au premier lancement.
      if (user) {
        setAgendaNote('Vous serez prévenu·e la veille par notification.');
        if (Capacitor.isNativePlatform()) {
          void requestStartupNotifications(async (token) => {
            await syncPushTokenForUser(user.id, token);
          }).catch(() => {});
        }
      } else {
        setAgendaNote('Connectez-vous pour recevoir un rappel la veille.');
      }
      window.setTimeout(() => setAgendaNote(null), 5000);
    } else {
      setAgendaNote(null);
    }
  }

  async function report() {
    if (!event) return;
    if (!confirm('Signaler cet événement comme inapproprié ?')) return;
    await supabase.from('reports').insert({ event_id: event.id, user_id: user?.id ?? null, reason: 'user_report' });
    alert('Merci, signalement enregistré.');
  }

  const meta = event ? categoryOf(event.kind) : null;
  const isFav = event ? fav.ids.has(event.id) : false;
  const price = !event ? null
    : event.is_free ? 'Gratuit'
    : event.price_min != null ? (event.price_max && event.price_max > event.price_min ? `${event.price_min}–${event.price_max} €` : `${event.price_min} €`)
    : null;
  // Uniquement le vrai site officiel — on n'expose plus de bouton « Voir la source ».
  const primaryLink = event?.url ?? null;
  const heroImage = event
    ? getResponsiveImageProps(event.image_url, { widths: [480, 768, 960], sizes: '100vw', fallbackWidth: 480, quality: 72 })
    : null;

  return (
    <>
    <AnimatePresence>
      {eventId && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-ink-1/40"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-paper rounded-t-2xl shadow-sheet flex flex-col"
            style={{ maxHeight: '92vh' }}
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          >
            {/* Drag handle + close */}
            <div className="shrink-0 flex justify-between items-center px-4 pt-3 pb-1">
              <div className="flex-1" />
              <div className="w-10 h-1 rounded-full bg-paper-edge" />
              <div className="flex-1 flex justify-end">
                <button
                  onClick={onClose}
                  className="h-9 w-9 rounded-full grid place-items-center bg-paper-elev ring-1 ring-paper-edge"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto scrollbar-none">
              {loading ? (
                <div className="px-5 pb-8 pt-2">
                  <Skeleton className="h-[35vh] w-full rounded-md" />
                  <div className="mt-4 space-y-3">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              ) : event ? (
                <div className="pb-4">
                  {/* Hero image */}
                  <div className="relative">
                    {event.image_url
                      ? <img src={heroImage?.src ?? event.image_url} srcSet={heroImage?.srcSet} sizes={heroImage?.sizes} alt="" className="w-full aspect-[16/9] object-cover" fetchPriority="high" decoding="async" />
                      : <FallbackPoster kind={event.kind} title={event.title} city={event.city} startsAt={event.starts_at} ratio="landscape" className="!aspect-[16/9] !h-auto" />}
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                      <button onClick={share} aria-label="Partager" className="h-10 w-10 rounded-full grid place-items-center bg-paper-elev/90 backdrop-blur ring-1 ring-paper-edge">
                        <ShareNetwork size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="px-5 -mt-4 relative">
                    <EditionBadge seed={event.id} className="text-paper-elev/95 drop-shadow" />
                  </div>

                  <div className="px-5 pt-5">
                    <div className="t-cap mb-2">
                      {meta?.label}
                      {event.audiences && event.audiences.length > 0 && <> · {event.audiences.map(a => AUDIENCE_LABEL[a]).join(' · ')}</>}
                    </div>
                    <h2 className="t-ed-3xl mb-4 leading-tight">{event.title}</h2>

                    <Button
                      variant={isFav ? 'outline' : 'secondary'}
                      full
                      iconLeft={isFav ? <Check size={18} weight="bold" /> : <CalendarPlus size={18} />}
                      className="mb-4"
                      onClick={toggleAgenda}
                    >
                      {isFav ? 'Ajouté à mon agenda' : 'Ajouter à mon agenda'}
                    </Button>
                    {agendaNote ? <p className="t-ui-xs text-ink-3 mt-2 text-center">{agendaNote}</p> : null}

                    <GoldenRule short />

                    <div className="mt-5 space-y-2.5 text-ink-2">
                      <div className="flex items-center gap-3">
                        <CalendarBlank size={18} className="text-ink-3" />
                        <span className="t-mono-sm">{formatRange(event.starts_at, event.ends_at).toUpperCase()}</span>
                      </div>
                      {hasKnownTime(event.starts_at) && (
                        <div className="flex items-center gap-3">
                          <Clock size={18} className="text-ink-3" />
                          <span className="t-mono-sm">{formatTime(event.starts_at)}{event.ends_at && hasKnownTime(event.ends_at) && ` – ${formatTime(event.ends_at)}`}</span>
                        </div>
                      )}
                      {(event.city || event.lat) && (
                        <div className="flex items-start gap-3">
                          <MapPin size={18} className="text-ink-3 mt-0.5" />
                          <div className="flex-1">
                            <div className="t-ui">{upperCity(event.city) ?? '—'}</div>
                            {event.lat && event.lon && (
                              <button type="button"
                                onClick={() => void openExternal(`https://www.openstreetmap.org/?mlat=${event.lat}&mlon=${event.lon}#map=16/${event.lat}/${event.lon}`)}
                                className="inline-flex items-center gap-1 mt-1 font-serif italic text-[14px] text-terre">
                                Itinéraire <ArrowUpRight size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {event.lat && event.lon && (
                      <div className="mt-5 rounded-md overflow-hidden ring-1 ring-paper-edge">
                        <EventMap
                          events={[event]}
                          center={{ lat: event.lat, lon: event.lon }}
                          height={140}
                          interactive={false}
                          className="pointer-events-none"
                        />
                      </div>
                    )}

                    {event.description && (
                      <>
                        <div className="mt-8 mb-3 flex items-center gap-3"><h3 className="t-ed-lg whitespace-nowrap">À propos</h3><GoldenRule /></div>
                        <p className="t-ui text-ink-2 whitespace-pre-line">{event.description}</p>
                      </>
                    )}

                    {org && (
                      <>
                        <div className="mt-10 mb-4 flex items-center gap-3"><h3 className="t-ed-lg whitespace-nowrap">Organisé par</h3><GoldenRule /></div>
                        <div className="flex items-center gap-3">
                          <Monogram name={org.name} size={48} />
                          <div className="flex-1 min-w-0">
                            <div className="t-ed-md truncate">{org.name}</div>
                            {org.city ? <span className="t-ui-xs text-ink-3">{upperCity(org.city)}</span> : null}
                          </div>
                        </div>
                      </>
                    )}

                    <AdBannerDetail adKey={`sheet-detail-${eventId ?? 'x'}`} />

                    <div className="mt-10 flex items-center gap-4">
                      <TamponJyEtais />
                      <button onClick={report} className="ml-auto inline-flex items-center gap-1.5 t-ui-sm font-serif italic text-ink-3 hover:text-cerise">
                        <Warning size={14} /> Une erreur ? <span className="underline">prévenez-nous</span>
                      </button>
                    </div>

                    {also.length > 0 && (
                      <>
                        <div className="mt-12 mb-4 flex items-center gap-3"><h3 className="t-ed-lg whitespace-nowrap">À voir aussi</h3><GoldenRule /></div>
                        <div className="flex gap-4 overflow-x-auto -mx-5 px-5 pb-2 scrollbar-none snap-x snap-mandatory">
                          {also.map(e => <div key={e.id} className="snap-start"><EventCard event={e} variant="peek" /></div>)}
                        </div>
                      </>
                    )}

                    <div className="mt-10">
                      <SourceFooter source={event.source} attribution={event.attribution} license={event.license} lastSyncedAt={event.last_synced_at} />
                    </div>

                    <div className="mt-4 rounded-md bg-paper-elev ring-1 ring-paper-edge p-4">
                      <div className="inline-flex items-center gap-2 t-cap text-ink-3">
                        <Warning size={14} />
                        À vérifier avant d&apos;y aller
                      </div>
                      <p className="mt-2 t-ui-sm text-ink-2">
                        Les informations de cet événement peuvent évoluer. Avant de vous déplacer, vérifiez les horaires,
                        tarifs, conditions d&apos;accès et éventuelles annulations auprès de l&apos;organisateur ou sur la source
                        officielle. Gazette référence ces informations à titre indicatif et ne peut garantir l&apos;absence de
                        modification par des tiers.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Sticky bottom CTA */}
            {event && (
              <div className="shrink-0 bg-paper-elev/95 backdrop-blur border-t border-paper-edge"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                <div className="px-5 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0 leading-tight">
                    {price === 'Gratuit'
                      ? <div className="font-serif italic text-[18px] text-terre leading-none">Gratuit</div>
                      : price ? <div className="t-mono-sm text-ink-1 leading-none">{price}</div>
                      : <div className="t-ui-xs text-ink-3 italic font-serif leading-none">Tarif sur place</div>}
                    <div className="t-ui-xs text-ink-3 mt-1 truncate">{formatDayLong(event.starts_at)}</div>
                  </div>
                  {primaryLink && (
                    <button type="button" onClick={() => void openExternal(primaryLink)} className="shrink-0 inline-flex items-center gap-2 h-11 px-4 rounded-md bg-terre text-paper-elev font-medium text-[14px]">
                      Site officiel <ArrowUpRight size={16} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>

    <ShareEventModal event={event} open={shareOpen} onClose={() => setShareOpen(false)} />
    </>
  );
}
